import { useEffect, useMemo, useRef, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { useAuth } from '../auth/AuthProvider';
import { FIRM_DOMAIN, isFirmAddress, normalise, resolveTyped } from '../lib/copyOthers';

/**
 * Who else is copied on a conflict check request.
 *
 * A FIELD, NOT A BUTTON THAT OPENS ONE. The first version was a button with a count badge that
 * revealed an input when pressed: two steps to add somebody, and once added they were a number
 * rather than a name. Who is being copied is exactly the thing that should be readable without
 * clicking anything — it is the only part of this form that sends the request to somebody else.
 *
 * STICKY PER PERSON. Somebody who copies their secretary on one request copies her on the next;
 * making them pick the same person every time is the kind of small repetition that makes a form
 * feel like paperwork. The default lives in `userPreferences/{uid}` — the person's own document,
 * which only they can read or write.
 *
 * THE REQUEST STILL RECORDS ITS OWN LIST. The preference seeds the NEXT request and nothing more;
 * what a submitted request recorded is who was copied on THAT one.
 *
 * FIRM ADDRESSES ONLY, and the server enforces it as well as this does. The request carries the
 * names and entities being checked; a free-text address box is the shortest path from "copy my
 * secretary" to that leaving the firm on a mistyped domain.
 *
 * NOTHING TYPED IS EVER DROPPED. What is still in the box is reported to the page through
 * `onPendingChange`, and the page refuses to submit while any remains. The rules for turning
 * typed text into a person are in lib/copyOthers.js, with the story of why.
 */

const PREF_KEY = 'defaultNotifyCcEmails';
const INPUT_ID = 'copy-others-input';

export default function CopyOthers({ value, onChange, onPendingChange, disabled, label = 'Copy others' }) {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [problem, setProblem] = useState('');
  const [focused, setFocused] = useState(false);
  /**
   * Matches from the firm's people index, via the searchDirectory callable.
   *
   * THE WHOLE FIRM, NOT THE ROSTER. The roster is people with a role in this app, mostly
   * attorneys, and the person somebody most wants to copy is their secretary, who was never in
   * it. The index is every real person at the firm (functions/src/roster/directoryIndex.js has
   * the rule), and it is searched server-side because the list itself is never sent to a browser.
   *
   * `known` remembers every name seen for an address, so a chip still shows a name after the
   * query that found it has moved on.
   */
  const [matches, setMatches] = useState([]);
  // The text `matches` were fetched for. Only results for exactly the current text may turn it
  // into a person; the list lags the typing, and older results would pick the wrong one.
  const [matchesTerm, setMatchesTerm] = useState('');
  const [known, setKnown] = useState({});
  const latestTerm = useRef('');

  const selected = useMemo(() => (Array.isArray(value) ? value : []), [value]);
  // Read after an await, when the render's own `selected` may already be out of date.
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  /**
   * The person's saved default, applied only to a request with no list of its own.
   *
   * `seeded` guards against re-applying it after somebody deliberately removes everybody: without
   * it, clearing the list would silently refill from the preference on the next render, and the
   * control would appear to ignore them.
   */
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || !user || selected.length) return;
    seeded.current = true;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'userPreferences', user.uid));
        const saved = snap.exists() ? snap.data()[PREF_KEY] : null;
        const clean = (Array.isArray(saved) ? saved : []).map(normalise).filter(isFirmAddress);
        if (!cancelled && clean.length) onChange(clean);
      } catch {
        /* a default that cannot be read is simply not applied */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  /** Remembers this list as the default for next time. Failure is silent and costs nothing. */
  const remember = (next) => {
    if (!user) return;
    setDoc(doc(db, 'userPreferences', user.uid), { [PREF_KEY]: next }, { merge: true }).catch(() => {});
  };

  const set = (next) => {
    onChange(next);
    remember(next);
  };

  /** Applies what typed text resolved to: a person on the list, or the reason it is not one. */
  const commit = (result) => {
    if (result.add) {
      setProblem('');
      setQuery('');
      set([...selectedRef.current, result.add]);
      return true;
    }
    if (result.problem) setProblem(result.problem);
    return false;
  };

  /** A picked or typed address, through the same checks as everything else. */
  const add = (email) =>
    commit(resolveTyped({ text: email, people: null, selected: selectedRef.current, self: user?.email }));

  const remove = (email) => {
    setProblem('');
    set(selected.filter((e) => e !== email));
  };

  /**
   * One search. Its results are kept only while they still describe what is in the box: a slow
   * answer for older text must not replace a newer one. Returns null when the search failed.
   */
  const lookup = async (term) => {
    try {
      const result = await httpsCallable(functions, 'searchDirectory')({ query: term });
      const people = (result.data && result.data.people) || [];
      setKnown((prev) => {
        const next = { ...prev };
        for (const person of people) next[normalise(person.email)] = person.name;
        return next;
      });
      if (latestTerm.current === term) {
        setMatches(people);
        setMatchesTerm(term);
      }
      return people;
    } catch {
      if (latestTerm.current === term) setMatches([]);
      return null;
    }
  };

  // Debounced, because every keystroke is otherwise a Cloud Function call. 200ms is under what
  // anyone notices and over what typing produces.
  useEffect(() => {
    const term = normalise(query);
    latestTerm.current = term;
    if (term.length < 2) {
      setMatches([]);
      setMatchesTerm('');
      return undefined;
    }
    const timer = setTimeout(() => lookup(term), 200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // What is still typed goes up to the page, which will not submit while any of it remains.
  useEffect(() => {
    if (onPendingChange) onPendingChange(query.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  /** Enter: make what is typed a person now, searching first if the list has not caught up. */
  const resolveNow = async () => {
    const typed = query;
    const term = normalise(typed);
    if (!term) return;
    const current = matchesTerm === term ? matches : null;
    let result = resolveTyped({ text: typed, people: current, selected: selectedRef.current, self: user?.email });
    if (result.lookup) {
      const people = await lookup(term);
      // They kept typing while it looked; the newer text is resolved on its own Enter.
      if (latestTerm.current !== term) return;
      if (people === null) {
        setProblem(`Could not look that up just now. Pick the name from the list, or type the full @${FIRM_DOMAIN} address.`);
        return;
      }
      result = resolveTyped({ text: typed, people, selected: selectedRef.current, self: user?.email });
    }
    commit(result);
  };

  const suggestions = useMemo(
    () =>
      matches
        .filter((person) => {
          const email = normalise(person.email);
          if (!email || !isFirmAddress(email) || selected.includes(email)) return false;
          return email !== normalise(user?.email);
        })
        .slice(0, 6),
    [matches, selected, user]
  );

  const nameFor = (email) => known[email] || email;

  return (
    <div>
      <label htmlFor={INPUT_ID} className="block text-sm font-medium text-slate-800 dark:text-slate-100">
        {label} <span className="font-normal text-slate-500 dark:text-slate-400">(optional)</span>
      </label>
      <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
        They are copied on this request, alongside the Conflicts team. Remembered for your next
        request.
      </p>

      {/*
        One box holding the names and the cursor, rather than a list and a separate input. Who is
        being copied is then the first thing read, which is the point of the change: a count said
        how many, not who.
      */}
      <div
        onClick={() => document.getElementById(INPUT_ID)?.focus()}
        className={`mt-2 flex flex-wrap items-center gap-1.5 rounded border px-2 py-1.5 ${
          disabled ? 'bg-slate-50 dark:bg-slate-900' : 'bg-white dark:bg-slate-900'
        } ${focused ? 'border-firm-500 ring-1 ring-firm-500' : 'border-slate-300 dark:border-slate-600'}`}
      >
        {selected.map((email) => (
          <span
            key={email}
            data-chip
            className="inline-flex max-w-full items-center gap-1 rounded bg-firm-700 px-2 py-0.5 text-xs text-white dark:bg-firm-500"
          >
            <span className="truncate">{nameFor(email)}</span>
            <button
              type="button"
              disabled={disabled}
              onClick={(event) => {
                event.stopPropagation();
                remove(email);
              }}
              aria-label={`Remove ${nameFor(email)}`}
              className="shrink-0 text-firm-200 hover:text-white"
            >
              ×
            </button>
          </span>
        ))}

        <input
          id={INPUT_ID}
          type="text"
          disabled={disabled}
          className="min-w-[12rem] flex-1 border-0 bg-transparent p-0.5 text-sm outline-none placeholder:text-slate-400 dark:text-slate-100"
          placeholder={selected.length ? 'Add another…' : `Name or @${FIRM_DOMAIN} address`}
          value={query}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            // A full address typed and left is what was meant, and is added as written. A name is
            // not guessed at on the way out: the page stops the submit and asks instead.
            if (normalise(query).includes('@')) add(query);
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setProblem('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              resolveNow();
            }
            // Backspace on an empty box removes the last one: how every field of this shape
            // behaves, and how people try it first.
            if (e.key === 'Backspace' && !query && selected.length) {
              remove(selected[selected.length - 1]);
            }
          }}
        />
      </div>

      {suggestions.length > 0 && (
        <ul className="mt-1 max-h-44 overflow-y-auto rounded border border-slate-200 dark:border-slate-700">
          {suggestions.map((person) => (
            <li key={person.email}>
              <button
                type="button"
                // Keeps the focus in the box, so picking from the list is not also a blur.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => add(person.email)}
                className="block w-full px-2 py-1 text-left hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <span className="text-sm text-ink dark:text-slate-100">{person.name || person.email}</span>
                <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
                  {[person.email, person.title, person.department].filter(Boolean).join(' · ')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {problem && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{problem}</p>}
    </div>
  );
}

export { PREF_KEY, INPUT_ID };
